/**
 * Gate 1 readiness: a section's starred fields are required only after the
 * user has entered anything in that section. Empty sections do not block Next.
 */

import { isApiSurfaceInUse } from "./apiSurfaceForm";
import { isDbSurfaceInUse } from "./dbSurfaceForm";
import { validateApiBaseUrl } from "./validateApiBaseUrl";
import { fieldsForProvider, isVectorStoreInUse } from "./vectorStoreSurfaceForm";

function isBlank(value) {
  return !String(value ?? "").trim();
}

function apiRequiredErrors(apiForm = {}) {
  if (!isApiSurfaceInUse(apiForm)) return {};
  const api = {};
  if (isBlank(apiForm.baseUrl)) {
    api.baseUrl = "API base URL is required";
  } else {
    const baseCheck = validateApiBaseUrl(apiForm.baseUrl);
    if (!baseCheck.ok) api.baseUrl = baseCheck.error;
  }
  if (isBlank(apiForm.authType)) api.authType = "Authentication type is required";

  /* Endpoint required checks stay in this helper for later restore; they do
   * not currently block Next or drive the Endpoints card mandatory UI. */
  return api;
}

function dbRequiredErrors(dbForm = {}, dbCredentials = {}) {
  if (!isDbSurfaceInUse(dbForm, dbCredentials)) return {};
  const db = {};
  if (isBlank(dbForm.dbType)) db.dbType = "Database type is required";
  if (isBlank(dbForm.database)) db.database = "Database name is required";
  if (isBlank(dbForm.host)) db.host = "Database host is required";
  if (isBlank(dbForm.port)) {
    db.port = "Port is required";
  } else {
    const portNum = Number(String(dbForm.port).trim());
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      db.port = "Port must be between 1 and 65535";
    }
  }
  if (isBlank(dbForm.username)) db.username = "Username is required";
  if (isBlank(dbCredentials.password)) db.password = "Password is required";
  return db;
}

function vectorRequiredErrors(vectorForm = {}, vectorCredentials = {}) {
  if (!isVectorStoreInUse(vectorForm, vectorCredentials)) return {};
  const vector = {};
  if (isBlank(vectorForm.provider)) {
    vector.provider = "Provider is required";
    return vector;
  }
  fieldsForProvider(vectorForm.provider).forEach((field) => {
    if (!field.required) return;
    const value = field.credential ? vectorCredentials[field.key] : vectorForm[field.key];
    if (isBlank(value)) vector[field.key] = `${field.label} is required`;
  });
  return vector;
}

/**
 * @returns {{ api: object, db: object, vector: object }}
 */
export function requiredFieldErrorsForReadiness({
  apiForm = {},
  dbForm = {},
  dbCredentials = {},
  vectorForm = {},
  vectorCredentials = {},
} = {}) {
  return {
    api: apiRequiredErrors(apiForm),
    db: dbRequiredErrors(dbForm, dbCredentials),
    vector: vectorRequiredErrors(vectorForm, vectorCredentials),
  };
}

export function hasReadinessRequiredFieldErrors(errors = {}) {
  return (
    Object.keys(errors.api || {}).length > 0 ||
    Object.keys(errors.db || {}).length > 0 ||
    Object.keys(errors.vector || {}).length > 0
  );
}
